import { inject, Injectable } from '@angular/core';
import { from, map, Observable } from 'rxjs';
import { VECTOR_TILE_ROOT_URL } from '../../data-protocol-services/twc-vector-tile.data.service';

import Basemap from '@arcgis/core/Basemap';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Map from '@arcgis/core/Map';
import { ToolkitApiService } from '@jeppesen-foreflight/jmkw-toolkit';
import { ThemeColor } from '@jeppesen-foreflight/jmkw-types';
import {
  TwcResponseData,
  TwcUpdateData
} from '../../data-protocol-services/twc.data.types';
import { TwcTimeValidationService } from '../../twc-time-validation.service';
import {
  initialTime,
  normalizeSelectedFlightLevel,
  normalizeSelectedTime
} from '../../twc.mixin';
import { TwcLayerServiceInterface } from '../../twc.refresh-layer-plugin.service';
import { TwcVectorTileLayer } from './twc-vector-tile.layer';
import { TWC_CONFIG_TOKEN } from '../../plugins/twc-config.token';
import {
  createInitialLayerParams,
  getProductIdForFlightLevel
} from '../../twc-utils/layer-creation.utils';
import {
  buildTwcUrlParams,
  paramsObjectToArcgisUrlTemplate
} from '../../twc-utils/url.utils';

const DARK_BASEMAPS_TITLES = new Set([
  'darkGray',
  'darkGrayVector',
  'gray',
  'grayVector'
]);

@Injectable({
  providedIn: 'root'
})
export class TwcVectorTileLayerService
  implements TwcLayerServiceInterface<TwcVectorTileLayer>
{
  private readonly _api = inject(ToolkitApiService);
  private readonly _env = inject(TWC_CONFIG_TOKEN);
  private readonly _twcApiKey = this._env.apiKey;
  private readonly _timeValidationService = inject(TwcTimeValidationService);

  createLayer$<TwcVectorTileLayer>({
    twcProduct,
    timeMap,
    tsInSeconds,
    flightLevelValues
  }: TwcResponseData): Observable<TwcVectorTileLayer> {
    // Get initial time
    const initialTimeValue = initialTime(timeMap);

    // Create base layer parameters using utility
    const layerParams = createInitialLayerParams(
      twcProduct,
      timeMap,
      flightLevelValues
    );

    // Create validated URL
    const url = this._createFeatureUrl(
      twcProduct.defaultProductId,
      this._timeValidationService.getValidTimeForProduct(
        twcProduct.defaultProductId,
        tsInSeconds || initialTimeValue
      )
    );

    // Create layer style with source
    const style = this._createLayerStyle(twcProduct, url);

    // Create the layer
    const layer = new TwcVectorTileLayer({
      ...layerParams,
      style,
      customParameters: this._createCustomParameters()
    });

    // Load layer and set up basemap tracking
    return from(layer.load()).pipe(
      map((layer) => {
        this._trackBasemapChanges(layer, this._api.getMap());
        return layer as TwcVectorTileLayer;
      })
    );
  }

  updateLayer(
    layer: TwcVectorTileLayer,
    updateData: TwcUpdateData
  ): TwcVectorTileLayer {
    const selectedFlightLevel = normalizeSelectedFlightLevel(
      updateData.flightLevelValues,
      updateData.selectedFlightLevel,
      layer.selectedFlightLevel
    );
    const selectedTime = normalizeSelectedTime(
      updateData.timeMap,
      updateData.selectedTime,
      layer.selectedTime
    );

    // Update layer properties
    layer.updateFlightLevels(updateData.flightLevelValues, selectedFlightLevel);
    layer.updateTimeMap(updateData.timeMap, selectedTime);

    // Get product ID for the selected flight level using utility
    const productId = getProductIdForFlightLevel(
      updateData.twcProduct,
      selectedFlightLevel
    );

    // Create validated URL
    const validTime = this._timeValidationService.getValidTimeForProduct(
      productId,
      selectedTime
    );
    const url = this._createFeatureUrl(productId, validTime);

    // Update layer style
    const style = this._createLayerStyle(
      updateData.twcProduct,
      url,
      layer.style
    );
    layer.loadStyle(style);

    return layer;
  }

  /**
   * Create custom parameters for the vector tile layer
   */
  private _createCustomParameters(): Record<string, string | number> {
    return {
      format: 'mvt',
      lod: '{z}',
      x: '{x}',
      y: '{y}',
      'tile-size': 256,
      apiKey: this._twcApiKey ?? 'api key not set'
    };
  }

  /**
   * Create a feature URL with validated parameters
   */
  private _createFeatureUrl(productId: string, time: string): string {
    // Use buildTwcUrlParams utility for parameter building
    const baseParams = { apiKey: this._twcApiKey };

    // Only add time if it has a value
    const timeParams = time ? { time } : {};

    // Build params using utility
    const params = buildTwcUrlParams(baseParams, timeParams);

    return `${VECTOR_TILE_ROOT_URL}/${productId}/features?${paramsObjectToArcgisUrlTemplate(params)}`;
  }

  /**
   * Create or update a layer style
   */
  private _createLayerStyle(
    twcProduct: any,
    url: string,
    existingStyle?: any
  ): any {
    return {
      ...(existingStyle ?? twcProduct.layerSpecs?.vectorTile?.vectorStyle),
      sources: {
        twc: {
          name: 'TWC_Data',
          copyrightText: 'Sources: The Weather Company',
          tiles: [url],
          type: 'vector'
        }
      }
    };
  }

  /**
   * Set up basemap change tracking for style adjustments
   */
  private _trackBasemapChanges(layer: TwcVectorTileLayer, map: Map): void {
    const color = this._getBasemapColor(map.basemap);
    layer.jepTools.toggleVectorStyleColor(color);

    const handle = reactiveUtils.watch(
      () => this._getBasemapColor(map.basemap),
      (color) => layer.jepTools.toggleVectorStyleColor(color)
    );

    layer.addHandles(handle);
  }

  /**
   * Determine if a basemap is dark or light
   */
  private _getBasemapColor(
    newBasemap: Basemap | null | undefined
  ): ThemeColor | undefined {
    if (!newBasemap?.id) return;
    return DARK_BASEMAPS_TITLES.has(newBasemap.id) ||
      DARK_BASEMAPS_TITLES.has(newBasemap.title)
      ? 'dark'
      : 'light';
  }
}
